import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAllPages } from "../apiClient";

const AGENTS_PATH = "/api/v1/agents?scope=all";

/** Choose agents by name (ids are stored) — for a connection grant, or an agent's sub-agents.
 * Loads every page of the cursor-paginated agent list; ids that no longer resolve stay visible so
 * they can be removed. ``onlyRunnable`` lists only agents the user may run; ``exclude`` hides ids. */
export default function AgentMultiSelect({ value, onChange, disabled = false, label = null, hint = null,
  onlyRunnable = false, exclude = [] }) {
  const { t } = useTranslation();
  const searchId = useId();
  const [agents, setAgents] = useState([]);
  const [settled, setSettled] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchAllPages(AGENTS_PATH)
      .then((items) => {
        if (cancelled) return;
        setAgents(items);
        setSettled(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setSettled(true);
      });
    return () => { cancelled = true; };
  }, []);

  const known = new Set(agents.map((a) => a.id));
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  const needle = query.trim().toLowerCase();
  const shown = agents.filter((a) => String(a.name || "").toLowerCase().includes(needle)
    && (!onlyRunnable || a.can_run) && !exclude.includes(a.id));

  return (
    <fieldset className="ds-field">
      <legend className="ds-label">{label || t("adminConnectors.agentIdsTitle")}</legend>
      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}
      <label className="ds-label" htmlFor={searchId}>{t("adminConnectors.agentPickerSearch")}</label>
      <input id={searchId} type="search" className="ds-input ds-input--sm" value={query} disabled={disabled}
             onChange={(e) => setQuery(e.target.value)} />
      {settled && value.filter((id) => !known.has(id)).map((id) => (
        <label key={id} className="cx-check-label">
          <input type="checkbox" checked disabled={disabled} onChange={() => toggle(id)} />{" "}
          {t("adminConnectors.agentPickerUnknown", { id })}
        </label>
      ))}
      {!settled && <span className="ds-help">{t("adminConnectors.agentPickerLoading")}</span>}
      {settled && !error && agents.length === 0 && <span className="ds-help">{t("adminConnectors.agentPickerEmpty")}</span>}
      {shown.map((a) => (
        <label key={a.id} className="cx-check-label">
          <input type="checkbox" checked={value.includes(a.id)} disabled={disabled} onChange={() => toggle(a.id)} />{" "}
          {a.name}
        </label>
      ))}
      <span className="ds-help">{hint || t("adminConnectors.agentIdsHint")}</span>
    </fieldset>
  );
}
